import React from 'react';
import { Tabs } from '~/shared/components/tabs/Tabs';
import { QueryParamTabs } from '~/shared/components/tabs/QueryParamTabs';
import { LegacyHospitalizationsTable } from './LegacyHospitalizationsTable';
import { LegacyHospitalizationVisitsTable } from './LegacyHospitalizationsEventTable';
import { Hospital } from 'lucide-react';
import { PageLayout } from '~/shared/PageLayout';
import { QueryDataTableFilterBar } from '~/shared/data-tables/query/QueryDataTableFilterBar';
import { FilterBar } from '~/shared/components/filter-bar/FilterBar';
import { FacilityFilter, LastStatusReceivedAsOfFilter, PatientFilter, StayStartedOnFilter, TeamFilter } from './Filters';
import type { SieveFilterModel } from '~/util/sieve/filter';
import { DataTableFilterInjectContextProvider } from '~/shared/data-tables/query/DataTableFilterInjectContext';
import { addDays } from 'date-fns';
import { convertClientDateToServerDate } from '~/util/date';
import { Button } from '~/shared/components/buttons/Button';
import { useMutation } from '~/util/http/useMutation';
import { confirmHospitalizationEventsOptions } from '@generated';
import { useQueryClient } from '@tanstack/react-query';
import { getSearchQueryKey } from '~/util/http/useSearch';

const views = [
	{ label: 'Hospitalization Events', value: 'events' },
	{ label: 'Hospital Stays', value: 'stays' }
];

export function LegacyHospitalizationsPage() {
	const lastWeek = convertClientDateToServerDate(addDays(new Date(), -7));
	const eventDefaultFilters: SieveFilterModel[] = [{ name: 'statusDate', operator: '>=', value: lastWeek }];
	const stayDefaultFilters: SieveFilterModel[] = [{ name: 'firstStatusDate', operator: '>=', value: lastWeek }];
	const [selectedEventIds, setSelectedEventIds] = React.useState<string[]>([]);
	const confirmEvents = useMutation(confirmHospitalizationEventsOptions);
	const queryClient = useQueryClient();

	const onConfirmEvents = () => {
		confirmEvents.mutate(
			{ eventIds: selectedEventIds },
			{
				onSuccess: () => {
					setSelectedEventIds([]);
					queryClient.invalidateQueries({ queryKey: getSearchQueryKey('careport-visits') });
				}
			}
		);
	};
	return (
		<PageLayout pageHeader={{ title: 'Hospitalizations', description: 'Manage re-hospitalizations.', TitleIcon: Hospital }}>
			<QueryParamTabs defaultTab={views[0].value} clearRemainingParams>
				<Tabs.TabList>
					{views.map(view => (
						<Tabs.Tab id={view.value} key={view.value}>
							{view.label}
						</Tabs.Tab>
					))}
				</Tabs.TabList>
				<Tabs.TabPanel id="events">
					<div className="space-y-6">
						<DataTableFilterInjectContextProvider defaultFilters={eventDefaultFilters}>
							<QueryDataTableFilterBar>
								<FilterBar.Item label="Patient">
									<PatientFilter />
								</FilterBar.Item>
								<FilterBar.Item label="Facility">
									<FacilityFilter />
								</FilterBar.Item>
								<FilterBar.Item label="Team">
									<TeamFilter />
								</FilterBar.Item>
								<FilterBar.Item label="Status Date As Of">
									<LastStatusReceivedAsOfFilter />
								</FilterBar.Item>
							</QueryDataTableFilterBar>
							<LegacyHospitalizationVisitsTable
								selection={{
									onSelectionChange: (selectedIds: string[]) => {
										setSelectedEventIds(selectedIds);
									},
									selectedIds: selectedEventIds,
									selectionToolbarSlot: (
										<Button
											appearance="outline"
											size="sm"
											onClick={onConfirmEvents}
											isDisabled={selectedEventIds.length === 0}
											label="Confirm Selected"
											isPending={confirmEvents.isPending}
										/>
									)
								}}
							/>
						</DataTableFilterInjectContextProvider>
					</div>
				</Tabs.TabPanel>
				<Tabs.TabPanel id="stays">
					<div className="space-y-6">
						<DataTableFilterInjectContextProvider defaultFilters={stayDefaultFilters}>
							<QueryDataTableFilterBar>
								<FilterBar.Item label="Patient">
									<PatientFilter />
								</FilterBar.Item>
								<FilterBar.Item label="Facility">
									<FacilityFilter />
								</FilterBar.Item>
								<FilterBar.Item label="Team">
									<TeamFilter />
								</FilterBar.Item>
								<FilterBar.Item label="Stay Started As Of">
									<StayStartedOnFilter />
								</FilterBar.Item>
							</QueryDataTableFilterBar>
							<LegacyHospitalizationsTable />
						</DataTableFilterInjectContextProvider>
					</div>
				</Tabs.TabPanel>
			</QueryParamTabs>
		</PageLayout>
	);
}
